import PagedTable from '../sorted-table/PagedTable';
import ActionMenu, { ActionButton } from '../action-buttons/ActionButtons';
import Loader from '../loader/Loader';
import { getOnFarmBilledAccountsOData } from '../../api/onFarmBillingApi';
import { StandardParams, StandardParamsToODataParams } from '../../types/interfaces';
import { TABLE_HEADER_CONSTANTS, ON_FARM_BILLING_CONSTANTS } from '../../constants/common';
import {
  formatOnFarmCents,
  formatOnFarmPlan,
  formatOnFarmValue,
  getOnFarmStatusClass,
  OnFarmBillingAccount,
  toPagedAccountsFromOnFarmBilling,
} from '../../pages/on-farm-billing-management/onFarmBillingManagement.utils';

type OnFarmBillingAccountsTableProps = {
  refresh: number;
  handleActionButtons: (account: OnFarmBillingAccount) => ActionButton[];
  accounts: OnFarmBillingAccount[];
  isLoading?: boolean;
  billedAccountsQuery?: { filter?: string };
};

const OnFarmBillingAccountsTable = ({
  refresh,
  handleActionButtons,
  accounts,
  isLoading = false,
  billedAccountsQuery = {},
}: OnFarmBillingAccountsTableProps) => {
  const getData = (params: StandardParams) => {
    return getOnFarmBilledAccountsOData(
      StandardParamsToODataParams({ ...params, filter: billedAccountsQuery.filter }),
    ).then(response => ({
      ...response,
      data: toPagedAccountsFromOnFarmBilling(response.data),
    }));
  };

  const renderRow = (account: OnFarmBillingAccount) => {
    const dueCents = account.onFarmOutstandingBalanceCents;
    const status = account.status;

    return {
      dueCents,
      status,
      plan: formatOnFarmPlan(account.isCustomQuote),
      monthlyFee: formatOnFarmCents(account.monthlyFeeCents),
      maxTanks: formatOnFarmValue(account.maxTanks),
      due: formatOnFarmCents(dueCents),
    };
  };

  const showEmptyState = !isLoading && accounts.length === 0;

  return (
    <>
      {showEmptyState ? (
        <div className="empty-accounts">
          <strong>{ON_FARM_BILLING_CONSTANTS.NO_ON_FARM_ACCOUNTS}</strong>
          <p>{ON_FARM_BILLING_CONSTANTS.EMPTY_ACCOUNTS_HINT}</p>
        </div>
      ) : (
        <>
          <div className="desk-on-farm-billing">
            <PagedTable
              key={billedAccountsQuery.filter ?? 'all'}
              headers={[
                { displayName: TABLE_HEADER_CONSTANTS.ACCOUNT },
                { displayName: ON_FARM_BILLING_CONSTANTS.PLAN, sortValue: 'IsCustomQuote' },
                { displayName: ON_FARM_BILLING_CONSTANTS.MONTHLY_FEE, sortValue: 'PlanMonthlyFeeCents' },
                { displayName: ON_FARM_BILLING_CONSTANTS.MAX_TANKS, sortValue: 'MaxTanks' },
                { displayName: ON_FARM_BILLING_CONSTANTS.ON_FARM_DUE, sortValue: 'OnFarmOutstandingBalanceCents' },
                { displayName: TABLE_HEADER_CONSTANTS.STATUS, sortValue: 'Status' },
                { displayName: TABLE_HEADER_CONSTANTS.ACTION },
              ]}
              getData={getData}
              pageSize={8}
              minHeight={80}
              height={360}
              refresh={refresh}
              noResultsMessage={ON_FARM_BILLING_CONSTANTS.NO_ON_FARM_ACCOUNTS}
              buildRow={account => {
                const row = renderRow(account);
                return [
                  <span key={`name-${account.accountId}`} className="account-name">
                    {account.name}
                  </span>,
                  <span key={`plan-${account.accountId}`} className="on-farm-tag">
                    {row.plan}
                  </span>,
                  row.monthlyFee,
                  row.maxTanks,
                  <span key={`due-${account.accountId}`} className={(row.dueCents ?? 0) > 0 ? 'is-due' : ''}>
                    {row.due}
                  </span>,
                  row.status ? (
                    <span key={`status-${account.accountId}`} className={getOnFarmStatusClass(row.status)}>
                      {row.status}
                    </span>
                  ) : (
                    ON_FARM_BILLING_CONSTANTS.NOT_PROVIDED
                  ),
                  <ActionMenu key={`actions-${account.accountId}`} actionButtons={handleActionButtons(account)} />,
                ];
              }}
            />
          </div>
          <div className="xs-on-farm-billing">
            {isLoading ? (
              <Loader loaderSize="small" addedSpace />
            ) : (
              accounts.map((account, index) => {
                const row = renderRow(account);
                return (
                  <article className="on-farm-billing-mobile-card" key={`account-${account?.accountId}-${index}`}>
                    <div className="on-farm-billing-mobile-card__header">
                      <div>
                        <h4>{account?.name}</h4>
                      </div>
                      <ActionMenu actionButtons={handleActionButtons(account)} />
                    </div>
                    <div className="on-farm-billing-mobile-card__meta">
                      <span className="on-farm-tag">{row.plan}</span>
                      {row.status && <span className={getOnFarmStatusClass(row.status)}>{row.status}</span>}
                    </div>
                    <div className="on-farm-billing-mobile-card__metrics">
                      <div>
                        <span>{ON_FARM_BILLING_CONSTANTS.MONTHLY_FEE}</span>
                        <strong>{row.monthlyFee}</strong>
                      </div>
                      <div>
                        <span>{ON_FARM_BILLING_CONSTANTS.MAX_TANKS}</span>
                        <strong>{row.maxTanks}</strong>
                      </div>
                      <div>
                        <span>{ON_FARM_BILLING_CONSTANTS.ON_FARM_DUE}</span>
                        <strong className={(row.dueCents ?? 0) > 0 ? 'is-due' : ''}>{row.due}</strong>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </>
      )}
    </>
  );
};

export default OnFarmBillingAccountsTable;
